import React, {useState} from 'react';
import {adminGetSsoAccounts, adminMergeSsoAccounts} from '../../../services/api/admin';
import LoadSpinner from "../../../components/Common/LoadSpinner";
import PaginatedTable from "../../../components/Block/Table/PaginatedTable";
import MergeSSOAccountsModal from "../../../components/Admin/MergeSSOAccountsModal";

const headers = [
    {data: 'Tenant Id', classes: 'sl-font-bold'},
    {data: 'Tenant Name', classes: 'sl-font-bold'},
    {data: 'Potential Duplicates', classes: 'sl-font-bold'},
    {data: 'Action', classes: 'sl-font-bold'},
];

const SSOAccountsTable = () => {

    const [accountsData, setAccountsData] = useState([]);
    const [accountsFetched, setAccountsFetched] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const handleRowClick = (item) => {
        setSelectedRowData(item);
        setShowMergeModal(true);
    };

    const handleMergeAccountsClick = () => {
        setSelectedRowData(null);
        setShowMergeModal(true);
    };

    const handleCloseModal = () => {
        setShowMergeModal(false);
        setSelectedRowData(null);
    };

    const handleDryRun = async (mergeFromAccount, mergeIntoAccount) => {
        try {
            const result = await adminMergeSsoAccounts(
                mergeIntoAccount.account_id,   // original_account_id (INTO)
                mergeFromAccount.account_id,   // duplicate_account_id (FROM)
                true                           // dry_run
            );

            if (result && result.status) {
                return result;
            } else {
                throw new Error('Dry run failed');
            }
        } catch (error) {
            console.error('Error during dry run:', error);
            alert('Error during dry run. Please try again.');
            throw error;
        }
    };

    const handleMerge = async (mergeFromAccount, mergeIntoAccount) => {

        const fromEmail = mergeFromAccount.email;
        const intoEmail = mergeIntoAccount.email;
        const fromName = mergeFromAccount.rawData ?
            `${mergeFromAccount.rawData.first_name || ''} ${mergeFromAccount.rawData.last_name || ''}`.trim() : '';
        const intoName = mergeIntoAccount.rawData ?
            `${mergeIntoAccount.rawData.first_name || ''} ${mergeIntoAccount.rawData.last_name || ''}`.trim() : '';

        const confirmed = window.confirm(
            `⚠️ MERGE CONFIRMATION ⚠️\n\n` +
            `This will merge:\n\n` +
            `FROM Account:\n  Email: ${fromEmail}\n  Name: ${fromName || 'N/A'}\n\n` +
            `INTO Account:\n  Email: ${intoEmail}\n  Name: ${intoName || 'N/A'}\n\n` +
            `This action may be IRREVERSIBLE!\n\n` +
            `Are you absolutely sure you want to proceed?`
        );

        if (!confirmed) {
            throw new Error('User cancelled merge');
        }

        try {
            const result = await adminMergeSsoAccounts(
                mergeIntoAccount.account_id,   // original_account_id (INTO)
                mergeFromAccount.account_id,   // duplicate_account_id (FROM)
                false                          // dry_run = false
            );

            if (result && result.status) {
                setAccountsData([]);
                setAccountsFetched(false);
                return result;
            } else {
                throw new Error('Merge failed');
            }
        } catch (error) {
            console.error('Error merging accounts:', error);
            alert('Error merging accounts. Please try again.');
            throw error;
        }
    };

    const fetchSsoAccounts = async () => {

        if (!accountsData.length && !accountsFetched && !isFetching)
            try {
                setIsFetching(true);
                const {data} = await adminGetSsoAccounts();
                const transformedData = data.map((item) => {
                    const tempData = {};

                    tempData.tenant_id = {
                        data: item.tenant_id,
                        classes: "sl-min-w-10"
                    };

                    tempData.tenant_name = {
                        data: item.tenant_name,
                        classes: "sl-min-w-10"
                    };

                    tempData.potential_duplicates = {
                        data: <div className={"flex flex-col"}> {
                            item.accounts.map(dup => <span>
                                {dup.email}
                            </span>)
                        }</div>,
                        classes: "sl-min-w-148 sl-max-w-148"
                    };

                    tempData.action = {
                        data: <div>
                            <button
                                className={"sl-btn-sm sl-btn-primary sl-font sl-p-2 "}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick(item);
                                }}
                            >
                                Merge Duplicates
                            </button>
                        </div>,
                        classes: "sl-min-w-10"
                    };

                    // Store original item data for modal
                    tempData._rawData = item;

                    return tempData;
                });

                setIsFetching(false);
                setAccountsFetched(true);
                setAccountsData(transformedData);

            } catch (e) {
                console.error('Error fetching :', e);
            }
    }


    React.useEffect(() => {
            fetchSsoAccounts().then(r => console.log('Accounts fetched'));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountsFetched]);


    return (
        <div>
            <main className="sl-main sl-mt-8">
                <div className={"sl-container sl-m-auto sl-my-8"}>
                    <h1 className={`sl-mb-10 sl-text-xl sl-text-gray-900`}>Managing Potential Duplicate Accounts</h1>
                    <div className="sl-container sl-my-8 sl-flex sl-flex-col sl-gap-3">
                        <div className="sl-flex sl-justify-end sl-mb-4">
                            <button
                                className="sl-btn sl-btn-primary sl-btn-md"
                                onClick={handleMergeAccountsClick}
                            >
                                Merge Duplicates
                            </button>
                        </div>
                        <div className={"sl-mt-2"}>
                            {accountsFetched ? (
                                <div className="sl-table-container">
                                    <PaginatedTable headers={headers} data={accountsData}/>
                                </div>
                            ) : (
                                <div className="sl-self-center sl-mt-3 sl-mb-21">
                                    <LoadSpinner/>
                                </div>
                            )}

                        </div>

                    </div>
                </div>
            </main>

            {/* Merge SSO Accounts Modal */}
            {showMergeModal && (() => {
                // If opened from a row, pre-populate with tenant and account data
                if (selectedRowData) {
                    // Sort accounts by created_dt to determine older (merge from) and newer (merge into)
                    const sortedAccounts = [...selectedRowData.accounts].sort((a, b) => {
                        const dateA = new Date(a.created_dt || a.created_at || a.date_created);
                        const dateB = new Date(b.created_dt || b.created_at || b.date_created);
                        return dateA - dateB; // Ascending order (oldest first)
                    });

                    // Older account should be merged FROM (will be deleted)
                    const premergeFromAccount = sortedAccounts[0] || null;
                    // Newer account should be merged INTO (will be kept)
                    const premergeIntoAccount = sortedAccounts[sortedAccounts.length - 1] || null;

                    return (
                        <MergeSSOAccountsModal
                            isOpen={showMergeModal}
                            onClose={handleCloseModal}
                            tenantData={{
                                tenant_id: selectedRowData.tenant_id,
                                tenant_name: selectedRowData.tenant_name,
                                tenant_type: selectedRowData.tenant_type
                            }}
                            premergeFromAccount={premergeFromAccount}
                            premergeIntoAccount={premergeIntoAccount}
                            onDryRun={handleDryRun}
                            onMerge={handleMerge}
                        />
                    );
                } else {
                    // Opened from "Merge Accounts" button - no pre-selected data
                    return (
                        <MergeSSOAccountsModal
                            isOpen={showMergeModal}
                            onClose={handleCloseModal}
                            tenantData={null}
                            premergeFromAccount={null}
                            premergeIntoAccount={null}
                            onDryRun={handleDryRun}
                            onMerge={handleMerge}
                        />
                    );
                }
            })()}
        </div>
    );
};

export default SSOAccountsTable;
